import { tva } from '@gluestack-ui/utils/nativewind-utils'
import React, { JSX } from 'react'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'
import { HStack } from '../hstack'
import { Pressable } from '../pressable'
import { Text } from '../text'

export interface Step {
    title: string
    content: JSX.Element
}

export type StepVariant = 'upcoming' | 'active' | 'completed'
export type DotsVariant = 'upcoming' | 'completed'

const circle = tva({
    base: 'size-11 items-center justify-center overflow-hidden rounded-full',
    variants: {
        variant: {
            upcoming:
                'bg-graphics-silver hover:bg-graphics-silver-hover active:bg-graphics-silver-pressed',
            active: 'border-2 border-solid border-aos-text-default bg-action-default hover:bg-action-hover active:bg-action-pressed',
            completed: 'bg-action-default hover:bg-action-hover active:bg-action-pressed',
        },
    },
})

export type StepIndicatorProps = {
    stepNumber: number | string
    label?: string
    variant?: StepVariant
    onPress?: () => void
    className?: string
    testID?: string
}

export const StepIndicator: React.FC<StepIndicatorProps> = ({
    stepNumber,
    label,
    variant = 'upcoming',
    onPress,
    className,
    testID,
}) => (
    <Pressable
        className={twMerge(
            'h-[67px] w-[70px] flex-col items-center justify-center gap-1',
            className,
        )}
        onPress={onPress}
        accessibilityRole='button'
        accessibilityState={{ selected: variant === 'active' }}
        testID={testID}
    >
        <Box className={circle({ variant })}>
            <Text
                size='h3'
                weight='medium'
                tone={variant === 'upcoming' ? 'on-graphics' : 'on-primary'}
            >
                {String(stepNumber)}
            </Text>
        </Box>
        {label ? (
            <Text size='caption' weight='medium' tone='default' className='text-center'>
                {label}
            </Text>
        ) : null}
    </Pressable>
)
StepIndicator.displayName = 'StepIndicator'

export type DotsProps = {
    variant?: DotsVariant
    className?: string
    testID?: string
}

export const Dots: React.FC<DotsProps> = ({ variant = 'upcoming', className, testID }) => (
    <HStack
        testID={testID}
        className={twMerge('self-start h-11 items-center justify-center gap-3', className)}
    >
        {[0, 1, 2].map(i => (
            <Box
                key={i}
                className={twMerge(
                    'size-1.5 rounded-full',
                    variant === 'completed' ? 'bg-action-default' : 'bg-graphics-silver',
                )}
            />
        ))}
    </HStack>
)
Dots.displayName = 'Dots'

export type StepsProps = {
    steps: string[]
    activeIndex: number
    onStepPress?: (index: number) => void
    className?: string
    testID?: string
}

export const Steps: React.FC<StepsProps> = ({
    steps,
    activeIndex,
    onStepPress,
    className,
    testID,
}) => (
    <HStack testID={testID} className={twMerge('items-center gap-0.5', className)}>
        {steps.map((label, index) => {
            const variant: StepVariant =
                index < activeIndex ? 'completed' : index === activeIndex ? 'active' : 'upcoming'
            return (
                <React.Fragment key={index}>
                    {index > 0 && (
                        <Dots variant={index <= activeIndex ? 'completed' : 'upcoming'} />
                    )}
                    <StepIndicator
                        stepNumber={index + 1}
                        label={label}
                        variant={variant}
                        onPress={onStepPress ? () => onStepPress(index) : undefined}
                    />
                </React.Fragment>
            )
        })}
    </HStack>
)
Steps.displayName = 'Steps'
